<template>
  <SfMessageContainer
    :read="read"
    :has-opacity="todo && done && !slim"
    :slim="slim"
  >
    <template v-if="!slim" #top-left>
      <div class="tw-absolute tw-start-[-40px]">
        <slot name="top-left"></slot>
      </div>
    </template>
    <template v-if="!slim" #heading>
      <div class="tw-flex tw-gap-2 tw-items-center">
        <span
          ref="authorRef"
          v-tooltip="authorTooltip"
          class="
            tw-font-medium
            tw-text-sm
            tw-whitespace-nowrap
            tw-text-ellipsis
            tw-overflow-hidden
          "
          :class="hideSessionClass"
          data-testid="remark-card-flat-author-name"
        >
          <i v-if="createdByBot" class="i-bot"></i>
          {{ authorName }}
        </span>
        <time
          v-tooltip.timestamp="displayTime"
          class="tw-text-xs tw-text-sf-gray-light tw-whitespace-nowrap"
        >
          {{ displayTimeFormatted }}
        </time>
        <i
          v-if="sticky"
          v-tooltip="stickyIconTooltip"
          class="
            tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center
            fa fa-thumb-tack
            sf-bg-neutral-subtle-2
            tw-text-sf-gray-light tw-rounded
          "
        />
        <span
          v-if="recurring"
          v-tooltip="I18n.t('communication.cards.recurring_todo_tooltip_icon')"
          class="
            tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center
            sf-bg-neutral-subtle-2
            tw-rounded
          "
        >
          <RecurringTodoTooltipIcon
            class="remark-card-flat__recurring-todo-icon"
          />
        </span>
        <i
          v-if="hasCheckDiff"
          v-tooltip="I18n.t('communication.cards.hash_check_tooltip')"
          class="
            fa fa-info-circle
            tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center
            sf-bg-neutral-subtle-2
            tw-text-sf-gray-light tw-rounded
          "
        />
        <span
          v-if="hasDraft"
          v-tooltip="I18n.t('communication.cards.pending_draft_tooltip')"
          class="
            tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center
            sf-bg-neutral-subtle-2
            tw-text-sf-gray-light tw-rounded
          "
        >
          <i class="fa fa-edit remark-card-flat__has-draft-icon"></i>
        </span>
        <component
          :is="enableElementIconScroll ? 'button' : 'span'"
          v-if="reportElementName"
          v-tooltip="
            I18n.t('report.element_remark_tooltip', { name: reportElementName })
          "
          class="tw-flex tw-items-center tw-justify-center"
          v-bind="
            enableElementIconScroll
              ? {
                  type: 'button',
                  class: 'tw-border-0 tw-bg-transparent tw-p-0',
                  onClick: (event: MouseEvent) => {
                    event.stopPropagation()
                    $emit('scrollToElement')
                  },
                }
              : {}
          "
        >
          <i
            class="fa fa-indent"
            :class="
              enableElementIconScroll ? 'tw-text-sf-primary' : 'tw-text-black'
            "
          />
        </component>
        <span
          v-if="showPeriod && ledgerPeriodName"
          class="
            tw-px-2
            tw-py-1
            tw-font-normal
            tw-text-xs
            tw-border
            tw-rounded
            tw-border-solid
            tw-border-sf-neutral-light-1
            tw-whitespace-nowrap
          "
        >
          {{ ledgerPeriodName }}
        </span>
        <a
          v-if="allowedActions.openInContext && contextLink"
          ref="contextRef"
          v-tooltip="contextTooltip"
          :href="contextLink"
          class="
            tw-text-sm tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis
          "
          @click="trackClick('context-name')"
        >
          {{ contextText }}
        </a>
        <template v-if="requestedForOwner">
          <RemarkBubble
            v-if="pdfAnnotation.id"
            :id="'remark-' + id"
            :emit-click="true"
            :linking-path="pdfAnnotation.xfdfId"
            :prevent-bubble-unlink="preventBubbleUnlink"
            variant="default"
            @click.stop="gotoPdf"
          />
          <RemarkBubble
            :id="`remark-${id}`"
            :linking-path="liquidInputFieldPath"
            :prevent-bubble-unlink="preventBubbleUnlink"
          />
        </template>
        <template v-else>
          <div
            v-if="documentFilename"
            v-tooltip.once="filenameTooltip"
            class="tw-text-sf-primary-dark sf-pointer"
            data-testid="document-link"
            @click.stop="gotoPdf"
          >
            <span class="fa fa-file" />
          </div>
        </template>
      </div>
    </template>
    <template v-if="!slim" #before-message>
      <slot name="before-message"></slot>
    </template>
    <template #actions>
      <slot name="actions"></slot>
    </template>
    <template #body>
      <div
        class="remark-card-flat__rendered-text tw-text-sm tw-text-sf-gray"
        :class="messageClasses"
      >
        <div
          v-if="shouldShowReference"
          class="tw-text-xs tw-text-sf-gray-light"
        >
          <a
            v-if="contextLink"
            :href="contextLink"
            class="tw-text-sf-primary-dark hover:tw-underline"
            @click="trackClick('context-name', { skipBackInContext: true })"
          >
            {{ contextText }}
          </a>
          <template v-else>
            {{ contextText }}
          </template>
        </div>
        <div
          ref="renderedTextRef"
          v-dompurify-html:allowTarget="renderedText"
          :class="{ 'tw-ml-2': slim }"
        ></div>
      </div>

      <slot name="after-remark-text"></slot>

      <div class="tw-flex tw-flex-wrap tw-gap-1">
        <SfDocumentPill
          v-for="document in attachments"
          :id="document.id"
          :key="document.id"
          :viewing-url="document.url"
          :allow-delete="allowedActions.edit"
          @delete="emit('deleteAttachment', document.id)"
        >
          <template #left-icon>
            <i
              class="
                fa fa-file
                tw-text-xs tw-leading-4 tw-text-sf-gray-light
                hover:tw-text-sf-gray-light
              "
              data-testid="document-pill-file"
            ></i>
          </template>
          <template #default>
            <span :class="hideSessionClass">
              {{ document.filename }}
            </span>
          </template>
        </SfDocumentPill>
      </div>
      <div
        v-if="!slim && done && doneAt && doneByName"
        class="tw-uppercase tw-text-xs tw-font-medium tw-text-sf-dark-dark"
        :class="hideSessionClass"
      >
        {{ doneByText }}
      </div>
    </template>
  </SfMessageContainer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import usePulseMentionWarning, {
  type PulseMentionWarning,
} from '@app/shared/vue/composables/use-pulse-mention-warning'
import RecurringTodoTooltipIcon from '@app/shared/assets/icons/svg/recurring_todo_tooltip_icon.svg?component'
import RemarkBubble from '@app/application/communication/sections/communication/components/remarks/remark-bubble.vue'
import SfDocumentPill from '@app/shared/vue/components/sf-document-pill.vue'
import useDynamicTooltip from '@app/shared/vue/composables/use-dynamic-tooltip'
import useRedactSessionRecording from '@app/shared/vue/composables/use-hide-session-recording'
import useI18n from '@app/shared/vue/composables/use-I18n'
import communicationDate, {
  CommunicationDateFormats,
} from '@app/utils/communication-date'
import trackEvent from '@app/utils/track_event'
import SfMessageContainer from '../../sf-message/sf-message-container.vue'
import { useNavigationStore } from '@app/shared/vue/store/navigation-store'
import { isUsingNewLayout } from '@app/adapters/silverfin'

const I18n = useI18n()
const hideSessionClass = useRedactSessionRecording()

const emit =
  defineEmits<{
    deleteAttachment: [number]
    visitAnnotation: []
    scrollToElement: []
  }>()

interface Props {
  id: number
  showContext?: boolean
  contextText?: string
  contextLink?: string
  contextType?: string | null
  ledgerPeriodName?: string
  liquidInputFieldPath?: string | null
  authorName: string
  createdByBot?: boolean
  createdAt: string
  editedAt?: string
  renderedText?: string
  attachments: { url: string; filename: string; id: number }[]
  allowedActions: {
    edit: boolean
    openInContext: boolean
  }
  showPeriod?: boolean
  sticky?: boolean
  recurring?: boolean
  hasCheckDiff?: boolean
  hasDraft?: boolean
  pdfAnnotation?: {
    id: number
    xfdfId: string
    remarkId: number
    documentId: number
    userId: number
  }
  documentFilename?: string
  preventBubbleUnlink?: boolean
  read?: boolean
  done?: boolean
  doneAt?: string
  doneByName?: string
  todo?: boolean
  partOf?: string
  requestedForOwner?: boolean
  slim?: boolean
  reportElementName?: string
  enableElementIconScroll?: boolean
  pulseMentionWarning?: PulseMentionWarning | null
}

const props = withDefaults(defineProps<Props>(), {
  contextText: '',
  contextLink: '',
  contextType: null,
  documentFilename: '',
  liquidInputFieldPath: null,
  ledgerPeriodName: '',
  editedAt: '',
  renderedText: '',
  attachments: () => [],
  doneAt: '',
  doneByName: '',
  partOf: '',
  pdfAnnotation: () => ({
    id: 0,
    xfdfId: '',
    remarkId: 0,
    documentId: 0,
    userId: 0,
  }),
  slim: false,
  reportElementName: '',
  enableElementIconScroll: false,
  pulseMentionWarning: null,
})

const navigationStore = useNavigationStore()

const displayTime = computed(() => {
  return props.editedAt ? props.editedAt : props.createdAt
})

const displayTimeFormatted = computed(() => {
  return props.editedAt
    ? `${I18n.t('communication.cards.edited')} ${communicationDate(
        displayTime.value,
        CommunicationDateFormats.SHORT
      )}`
    : communicationDate(displayTime.value, CommunicationDateFormats.SHORT)
})

const doneByText = computed(() =>
  I18n.t('communication.cards.user_marked_as_done', { user: props.doneByName })
)

const messageClasses = computed(() => [
  hideSessionClass,
  {
    'tw-font-medium': !props.read,
    'tw-mt-2 tw-mb-2 tw-mr-1': props.slim,
  },
])

const stickyIconTooltip = computed(() => {
  if (props.todo) {
    return I18n.t('communication.cards.sticky_todo_tooltip_icon')
  } else {
    return I18n.t('communication.cards.sticky_note_tooltip_icon')
  }
})

const authorRef = ref<HTMLElement | null>(null)
const authorTooltip = useDynamicTooltip(authorRef, props.authorName)

const contextRef = ref<HTMLElement | null>(null)
const contextTooltip = useDynamicTooltip(contextRef, props.contextText ?? '')
const filenameTooltip = computed(() => {
  return I18n.t('remarks.message_attached_to') + ' ' + props.documentFilename
})

function gotoPdf() {
  if (props.pdfAnnotation.id) {
    emit('visitAnnotation')
  } else {
    window.open(props.contextLink, '_blank')
  }
}

function trackClick(label: string, { skipBackInContext = false } = {}) {
  const skipBack =
    skipBackInContext || (props.partOf === 'inbox' && isUsingNewLayout())

  if (!skipBack) {
    navigationStore.enableBackInContext(
      window.location.href,
      props.partOf,
      props.todo ? 'todo' : 'note'
    )
  }

  if (props.partOf) {
    trackEvent(props.partOf, 'click', label)
  }
}

const shouldShowReference = computed(() => {
  const excludedTypes = ['ClientMeeting']
  return (
    props.slim &&
    props.contextText &&
    !excludedTypes.includes(props.contextType || '')
  )
})

const renderedTextRef = ref<HTMLElement | null>(null)
usePulseMentionWarning(
  renderedTextRef,
  () => props.pulseMentionWarning,
  () => props.renderedText
)
</script>

<style lang="scss">
.remark-card-flat__rendered-text {
  word-break: break-word;

  blockquote {
    margin-bottom: 16px;
  }

  ul,
  ol {
    padding-left: 16px;
    margin-bottom: 16px;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  &:last-child {
    margin-bottom: 0 !important; // Override for default bootstrap style
  }
}

.remark-card-flat__recurring-todo-icon {
  margin-bottom: 0 !important;

  path {
    fill: var(--sf-color-text-light);
  }
}

.remark-card-flat__has-draft-icon {
  transform: translate(12%, 1%);
}
</style>
